import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { ChevronDown } from 'lucide-react';
import { Checkbox } from '@ui/components/ui/checkbox';
import { Loader } from '@ui';

import { cardHeaderClass, cardHeaderTitleClass, cardShellClass } from '@app/constants/styles';
import { StackedBarChart } from '../../clientReporting/product/charts';
import { monthLabelsIn } from '../../clientReporting/product/sources/sprintRange';
import { hoursShort, MAX_PEOPLE, opensWith, withDistinctColors, type PersonLine } from './productInsightsCards';

const card = `flex h-full flex-col ${cardShellClass}`;

/** Not the kit's Select: a Select announces itself as one choice out of a list, and this is many. */
const PeoplePicker = ({
  lines,
  chosen,
  onChange,
}: {
  lines: PersonLine[];
  chosen: string[];
  onChange: (next: string[]) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event: MouseEvent): void => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const toggle = (key: string): void =>
    onChange(chosen.includes(key) ? chosen.filter(each => each !== key) : [...chosen, key]);

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex h-7 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-[0.8rem] font-medium text-foreground shadow-xs hover:bg-muted"
      >
        {t('insights.cards.people.chosen', { count: chosen.length })}
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 max-h-64 w-60 overflow-y-auto rounded-xl border border-border bg-popover p-1 shadow-lg">
          <ul>
            {lines.map(line => {
              const ticked = chosen.includes(line.key);
              const full = !ticked && chosen.length >= MAX_PEOPLE;

              return (
                <li key={line.key}>
                  <button
                    type="button"
                    onClick={() => toggle(line.key)}
                    disabled={full}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Checkbox checked={ticked} aria-hidden tabIndex={-1} />
                    <span className="min-w-0 flex-1 truncate">{line.label}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {hoursShort(line.total)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

/** Why nothing is drawn: no sprint closed yet is not nobody having worked. */
const emptyKey = (lineCount: number, sprintCount: number): string => {
  if (lineCount) return 'insights.cards.people.noneChosen';
  return sprintCount ? 'insights.cards.people.none' : 'insights.cards.people.noneClosed';
};

/** "Jane Doe" as "Jane D", so two Janes on one product stay apart. */
const shortName = (name: string): string => {
  const [first, ...rest] = name.trim().split(/\s+/);
  const last = rest[rest.length - 1];
  return last ? `${first} ${last[0]}` : first;
};

/** A stacked bar per sprint, a segment per person, like the cost chart; it opens on the busiest of the last two sprints. */
const PeopleTimeCard = ({
  lines,
  sprints,
  year,
  isLoading,
  isError,
  className,
  action,
}: {
  lines: PersonLine[];
  sprints: { id: string; number: number; startDate: string }[];
  year: string;
  /** The year's reports are still arriving: an empty list is not yet an answer. */
  isLoading?: boolean;
  /** The year's reports did not arrive: an empty list is no answer at all. */
  isError?: boolean;
  /** What the card is worth in the row it sits in: the tab gives this one two thirds of its own. */
  className?: string;
  /** A control in the header's corner, after the picker, e.g. the arrow into Spend. */
  action?: React.ReactNode;
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  // Stamped with the roster it was made against: paging to a year without those people emptied the chart.
  const roster = lines.map(line => line.key).join('|');
  const [pick, setPick] = useState<{ keys: string[]; of: string } | null>(null);
  // Null until somebody picks: an empty list reads as a cleared chart, not as untouched.
  const chosen = pick && pick.of === roster ? pick.keys : null;
  // Stamped with the roster too, so another year's people open all shown.
  const [hiding, setHiding] = useState<{ keys: ReadonlySet<string>; of: string }>({ keys: new Set(), of: roster });
  const hidden = hiding.of === roster ? hiding.keys : new Set<string>();
  const setHidden = (next: (prev: ReadonlySet<string>) => ReadonlySet<string>): void =>
    setHiding(prev => ({ keys: next(prev.of === roster ? prev.keys : new Set()), of: roster }));
  // Picking someone is asking to see them, so it also undoes their legend toggle.
  const setChosen = (keys: string[] | null): void => {
    const before = chosen ?? opensWith(lines);
    setHidden(prev => new Set([...prev].filter(key => before.includes(key) || !keys?.includes(key))));
    setPick(keys === null ? null : { keys, of: roster });
  };
  const toggle = (key: string): void =>
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const opening = opensWith(lines);
  // Untouched, the card follows the figures: a year with new hours in it opens on whoever is busiest now.
  const shown = withDistinctColors(lines.filter(line => (chosen ?? opening).includes(line.key)));
  // Busiest over the year at the bottom of every bar, as the portfolio's time chart stacks.
  const bars = [...shown]
    .sort((a, b) => b.total - a.total)
    .map(line => ({ key: line.key, label: line.label, color: line.color, data: line.perSprint }));
  // Months under the bars, as the cost chart reads; the sprint's number is in the tooltip.
  const labels = monthLabelsIn(
    // A sprint's day at local midnight: a bare day parses as UTC and slips back a day west of Greenwich.
    sprints.map(sprint => new Date(`${sprint.startDate.slice(0, 10)}T00:00:00`)),
    i18n?.language,
  );

  return (
    <section className={cx(card, className)}>
      <div className={cardHeaderClass}>
        <div className={cardHeaderTitleClass}>
          <h3 className="text-base font-semibold text-foreground">{t('insights.cards.people.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('insights.cards.people.hint', { year })}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {lines.length > 0 && <PeoplePicker lines={lines} chosen={chosen ?? opening} onChange={setChosen} />}
          {action}
        </div>
      </div>

      {isLoading && (
        <div className="px-5 max-md:px-4 pb-5">
          <Loader />
        </div>
      )}
      {!isLoading && isError ? (
        <p className="px-5 max-md:px-4 pb-5 text-sm text-muted-foreground">{t('cardReadFailed')}</p>
      ) : null}
      {/* Four states, and exactly one of them: a loader over an empty chart is not a loading card. */}
      {!isLoading && !isError && !shown.length ? (
        <p className="px-5 max-md:px-4 pb-5 text-sm text-muted-foreground">
          {t(emptyKey(lines.length, sprints.length), { year })}
        </p>
      ) : null}
      {!isLoading && !isError && shown.length > 0 && (
        // Names under the bars, so the bars have the card's whole width.
        <div className="flex flex-1 flex-col gap-4 px-5 max-md:px-4 pb-5">
          <StackedBarChart
            series={bars}
            labels={labels}
            hiddenKeys={hidden}
            tip={(seriesIndex, barIndex, value) =>
              `${t('budget.sprintNumber', { number: sprints[barIndex]?.number ?? 0 })} · ${bars[seriesIndex].label}: ${hoursShort(value)}`
            }
            formatTick={hoursShort}
            // At least h-40, and taller when the card beside it sets a taller row.
            heightClass="min-h-40"
            className="min-h-0 w-full flex-1"
          />

          <ul className="flex flex-wrap gap-x-2 gap-y-1">
            {bars.map(bar => {
              const isHidden = hidden.has(bar.key);
              const total = bar.data.reduce((sum, hours) => sum + hours, 0);
              return (
                <li key={bar.key}>
                  <button
                    type="button"
                    onClick={() => toggle(bar.key)}
                    aria-pressed={!isHidden}
                    title={bar.label}
                    className={cx(
                      'flex items-center gap-2 rounded-md px-2 py-1 text-left transition duration-200 hover:bg-muted',
                      isHidden && 'opacity-45',
                    )}
                  >
                    {/* Filled when shown, hollow when hidden, as the cost chart's chapters. */}
                    <span
                      aria-hidden
                      className="size-3 shrink-0 rounded-[3px] transition-all duration-200"
                      style={{
                        backgroundColor: isHidden ? 'transparent' : bar.color,
                        boxShadow: `inset 0 0 0 1.5px ${bar.color}`,
                      }}
                    />
                    <span className="text-sm text-foreground">{shortName(bar.label)}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">{hoursShort(total)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

export default PeopleTimeCard;
